import { useFormat, useLocale, useTranslation } from '@dreamstack/i18n'
import { assertPopulated } from '@dreamstack/payload/src/lib/assertPopulated'
import { RouterOutputs } from '@dreamstack/trpc/client'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import type { NewsType } from './newsTypeUrls.static'
import { TransparentGradientBottomToTop } from '../layout/Gradients'
import { HeadingH2 } from '../layout/Texts'
import {
  StyledNewsDate,
  StyledNewsType,
  StyledNewsTypeWrapper,
} from './StyledNewsComponents'
import { ContinueReadingLink } from './ContinueReadingLink'
import { NewsHubPreview } from './NewsHubPreview'
import { getNewsPath } from './getNewsPath'

// TODO: Exchange with actual fallback img.
const fallbackImageUrl =
  'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1459&q=80'

export const FirstArticle: FunctionComponent<
  React.PropsWithChildren<{
    article: RouterOutputs['cms']['getNewsArticlePreviews']['items'][number]
  }>
> = ({ article }) => {
  assertPopulated(article.image)
  const formatDate = useFormat()
  const t = useTranslation()
  const locale = useLocale()
  if (!article) return null
  const { image, date, title } = article
  const slug = article.slug
  const newsType = article.newsType as NewsType

  if (!slug || !newsType) {
    // should never happen
    return null
  }

  const formattedDate = formatDate(date, 'dd. MMMM yyyy')
  const href = getNewsPath({ locale, slug, newsType })
  const translatedNewsType = t(`accentro:newsTypes.${newsType}`)

  return (
    <div className="mt-px-32 lg:mt-px-48">
      <div className="relative h-px-274 md:h-px-576 lg:mt-px-96 lg:h-px-576 items-end hidden md:flex">
        <Image
          className="object-cover"
          src={image?.url ?? fallbackImageUrl}
          fill
          alt={image?.alt || 'Newsarticle image'}
        />
        <TransparentGradientBottomToTop />
        <div className="p-px-64 z-10">
          <div className="flex items-center space-x-px-24">
            <StyledNewsTypeWrapper>
              <StyledNewsType>{translatedNewsType}</StyledNewsType>
            </StyledNewsTypeWrapper>
            {date && !article.hideDate && (
              <StyledNewsDate
                light
              >{`Berlin, ${formattedDate}`}</StyledNewsDate>
            )}
          </div>
          <HeadingH2 className="mt-px-32 text-accentroWhite-full">
            {title}
          </HeadingH2>
          <ContinueReadingLink whiteText href={href} file={article.file} />
        </div>
      </div>
      <div className="md:hidden">
        <NewsHubPreview article={article} />
      </div>
    </div>
  )
}
